
// Base template structure for version 1.0.0
const questionnaireTemplateV1 = {
  resourceType: "Questionnaire",
  id: "admission-history-physical",
  title: "Admission History & Physical",
  status: "active",
  version: "1.0.0",
  item: [
    {
      linkId: "administrative",
      text: "Administrative Details",
      type: "group",
      item: [
        { linkId: "patientName", text: "Patient Name", type: "string", required: true },
        { linkId: "visitDate", text: "Date of Admission", type: "date", required: true },
        { linkId: "attendingPhysician", text: "Attending Physician", type: "string", required: true }
      ]
    },
    {
      linkId: "chiefComplaint",
      text: "Chief Complaint",
      type: "group",
      item: [
        { linkId: "ccDescription", text: "Description", type: "text" },
        { linkId: "ccDuration", text: "Duration", type: "string" },
        { linkId: "ccSeverity", text: "Severity", type: "string" }
      ]
    },
    {
      linkId: "historyPresentIllness",
      text: "History of Present Illness (HPI)",
      type: "group",
      item: [
        { linkId: "hpiOnset", text: "Onset", type: "string" },
        { linkId: "hpiLocation", text: "Location", type: "string" },
        { linkId: "hpiDuration", text: "Duration", type: "string" },
        { linkId: "hpiCharacter", text: "Character/Quality", type: "string" },
        { linkId: "hpiSeverity", text: "Severity", type: "string" },
        { linkId: "hpiAggravating", text: "Aggravating Factors", type: "string" },
        { linkId: "hpiRelieving", text: "Relieving Factors", type: "string" },
        { linkId: "hpiTiming", text: "Timing/Patterns", type: "string" }
      ]
    },
    {
      linkId: "pastMedicalHistory",
      text: "Past Medical History",
      type: "group",
      item: [
        {
          linkId: "pmhChronicDiseases",
          text: "Chronic Diseases",
          type: "multiple-choice",
          answerOption: [
            { valueCoding: { code: "diabetes", display: "Diabetes" } },
            { valueCoding: { code: "hypertension", display: "Hypertension" } },
            { valueCoding: { code: "heartDisease", display: "Heart Disease" } },
            { valueCoding: { code: "asthma", display: "Asthma" } },
            { valueCoding: { code: "copd", display: "COPD" } },
            { valueCoding: { code: "arthritis", display: "Arthritis" } },
            { valueCoding: { code: "cancer", display: "Cancer" } },
            { valueCoding: { code: "depression", display: "Depression" } },
            { valueCoding: { code: "anxiety", display: "Anxiety" } },
            { valueCoding: { code: "thyroidDisorder", display: "Thyroid Disorder" } }
          ]
        },
        { linkId: "pmhOtherChronicIllnesses", text: "Other Chronic Illnesses", type: "text" },
        { linkId: "pmhSurgeries", text: "Past Surgeries (with dates)", type: "text" },
        { linkId: "pmhHospitalizations", text: "Prior Hospitalizations", type: "text" },
        { linkId: "pmhImmunizations", text: "Immunizations", type: "text" },
        { linkId: "pmhScreenings", text: "Preventive Screenings", type: "text" }
      ]
    },
    {
      linkId: "medications",
      text: "Current Medications",
      type: "group",
      repeats: true,
      item: [
        { linkId: "medName", text: "Name", type: "string" },
        { linkId: "medDose", text: "Dose", type: "string" },
        { linkId: "medRoute", text: "Route", type: "string" },
        { linkId: "medFrequency", text: "Frequency", type: "string" }
      ]
    },
    {
      linkId: "allergies",
      text: "Allergies / Adverse Reactions",
      type: "group",
      repeats: true,
      item: [
        { linkId: "allergySubstance", text: "Substance", type: "string" },
        { linkId: "allergyReaction", text: "Reaction", type: "string" },
        { linkId: "allergySeverity", text: "Severity", type: "string" }
      ]
    },
    {
      linkId: "familyHistory",
      text: "Family History",
      type: "group",
      repeats: true,
      item: [
        { linkId: "fhCondition", text: "Condition", type: "string" },
        { linkId: "fhRelative", text: "Relation", type: "string" },
        { linkId: "fhAgeAtOnset", text: "Age at Onset", type: "string" }
      ]
    },
    {
      linkId: "socialHistory",
      text: "Social History",
      type: "group",
      item: [
        { linkId: "shTobacco", text: "Tobacco Use (type/amount)", type: "string" },
        { linkId: "shAlcohol", text: "Alcohol Use (type/amount)", type: "string" },
        { linkId: "shIllicitDrugs", text: "Illicit Drug Use", type: "string" },
        { linkId: "shOccupation", text: "Occupation", type: "string" },
        { linkId: "shLivingSituation", text: "Living Situation", type: "string" },
        { linkId: "shExercise", text: "Exercise / Activity Level", type: "string" }
      ]
    },
    {
      linkId: "reviewSystems",
      text: "Review of Systems",
      type: "group",
      item: [
        {
          linkId: "rosConstitutional",
          text: "Constitutional (fever, weight change)",
          type: "string"
        },
        { linkId: "rosCardiac", text: "Cardiac (chest pain, palpitations)", type: "string" },
        { linkId: "rosRespiratory", text: "Respiratory (cough, dyspnea)", type: "string" },
        { linkId: "rosGastrointestinal", text: "GI (nausea, abdominal pain)", type: "string" },
        { linkId: "rosNeurological", text: "Neuro (headache, dizziness)", type: "string" }
      ]
    },
    {
      linkId: "physicalExam",
      text: "Physical Examination",
      type: "group",
      item: [
        {
          linkId: "vitals",
          text: "Vital Signs",
          type: "group",
          item: [
            { linkId: "vitalTemp", text: "Temperature", type: "string" },
            { linkId: "vitalHR", text: "Heart Rate", type: "string" },
            { linkId: "vitalBP", text: "Blood Pressure", type: "string" },
            { linkId: "vitalRR", text: "Respiratory Rate", type: "string" },
            { linkId: "vitalSpO2", text: "O₂ Saturation", type: "string" }
          ]
        },
        { linkId: "examGeneral", text: "General Appearance", type: "text" },
        { linkId: "examHEENT", text: "HEENT", type: "text" },
        { linkId: "examCardiovascular", text: "Cardiovascular", type: "text" },
        { linkId: "examRespiratory", text: "Respiratory", type: "text" },
        { linkId: "examAbdomen", text: "Abdomen", type: "text" },
        { linkId: "examExtremities", text: "Extremities", type: "text" },
        { linkId: "examSkin", text: "Skin", type: "text" },
        { linkId: "examNeuro", text: "Neurological", type: "text" }
      ]
    },
    {
      linkId: "assessmentPlan",
      text: "Assessment & Plan",
      type: "group",
      item: [
        { linkId: "assessment", text: "Assessment", type: "text" },
        { linkId: "diagnosis", text: "Diagnosis", type: "text" },
        { linkId: "plan", text: "Plan of Care", type: "text" },
      ]
    }
  ]
};

// Version 1.1.0 - Modified version with consolidated Past Medical History fields
const questionnaireTemplateV1_1 = {
  resourceType: "Questionnaire",
  id: "admission-history-physical",
  title: "Admission History & Physical",
  status: "active",
  version: "1.1.0",
  item: [
    {
      linkId: "administrative",
      text: "Administrative Details",
      type: "group",
      item: [
        { linkId: "patientName", text: "Patient Name", type: "string", required: true },
        { linkId: "visitDate", text: "Date of Admission", type: "date", required: true },
        { linkId: "attendingPhysician", text: "Attending Physician", type: "string", required: true }
      ]
    },
    {
      linkId: "chiefComplaint",
      text: "Chief Complaint",
      type: "group",
      item: [
        { linkId: "ccDescription", text: "Description", type: "text" }
      ]
    },
    {
      linkId: "historyPresentIllness",
      text: "History of Present Illness (HPI)",
      type: "group",
      item: [
        { linkId: "hpiOnset", text: "Onset, Location, Duration and Other Details", type: "text" }

      ]
    },
    {
      linkId: "pastMedicalHistory",
      text: "Past Medical History",
      type: "group",
      item: [
        {
          linkId: "pmhChronicDiseases",
          text: "Chronic Diseases",
          type: "multiple-choice",
          answerOption: [
            { valueCoding: { code: "diabetes", display: "Diabetes" } },
            { valueCoding: { code: "hypertension", display: "Hypertension" } },
            { valueCoding: { code: "heartDisease", display: "Heart Disease" } },
            { valueCoding: { code: "asthma", display: "Asthma" } },
            { valueCoding: { code: "copd", display: "COPD" } },
            { valueCoding: { code: "arthritis", display: "Arthritis" } },
            { valueCoding: { code: "cancer", display: "Cancer" } },
            { valueCoding: { code: "depression", display: "Depression" } },
            { valueCoding: { code: "anxiety", display: "Anxiety" } },
            { valueCoding: { code: "thyroidDisorder", display: "Thyroid Disorder" } }
          ]
        },
        { 
          linkId: "pmhOtherIllnesses", 
          text: "Other Medical History (surgeries, hospitalizations, immunizations, screenings, chronic illnesses)", 
          type: "text" 
        }
      ]
    },
    {
      linkId: "medications",
      text: "Current Medications",
      type: "group",
      //repeats: true,
      item: [
        { linkId: "medName", text: "Medications", type: "text" }
      ]
    },
    {
      linkId: "allergies",
      text: "Allergies / Adverse Reactions",
      type: "group",
      item: [
        { linkId: "allergySubstance", text: "Substance, Reaction and Severity", type: "text" },
      ]
    },
    {
      linkId: "familyHistory",
      text: "Family History",
      type: "group",
      //repeats: true,
      item: [
        { linkId: "fhCondition", text: "Condition, Relation and Age at Onset", type: "text" }
      ]
    },
    {
      linkId: "socialHistory",
      text: "Social History",
      type: "group",
      item: [
        { linkId: "shSocialHistory", text: "Tobacco, Alcohol, Drugs Use, Exercise, Diet and Other", type: "text" }
      ]
    },
    {
      linkId: "physicalExam",
      text: "Physical Examination",
      type: "group",
      item: [
        {
          linkId: "vitals",
          text: "Vital Signs",
          type: "group",
          item: [
            { linkId: "vitalTemp", text: "Temperature", type: "string" },
            { linkId: "vitalHR", text: "Heart Rate", type: "string" },
            { linkId: "vitalBP", text: "Blood Pressure", type: "string" },
            { linkId: "vitalRR", text: "Respiratory Rate", type: "string" },
            { linkId: "vitalSpO2", text: "O₂ Saturation", type: "string" }
          ]
        },
        { linkId: "examGeneral", text: "General Appearance", type: "text" },
        { linkId: "examHEENT", text: "ENT", type: "text" },
        { linkId: "examCardiovascular", text: "Cardiovascular", type: "text" },
        { linkId: "examRespiratory", text: "Respiratory", type: "text" },
        { linkId: "examAbdomen", text: "Abdomen", type: "text" },
        { linkId: "examExtremities", text: "Extremities", type: "text" },
        { linkId: "examSkin", text: "Skin", type: "text" },
        { linkId: "examNeuro", text: "Neurological", type: "text" }
      ]
    },
        {
      linkId: "assessmentPlan",
      text: "Diagnosis",
      type: "group",
      item: [
        { linkId: "diagnosis", text: "Diagnosis", type: "text" }
      ]
    }
  ]
};

// Registry of all questionnaire versions
export const questionnaireVersions = {
  "1.0.0": questionnaireTemplateV1,
  "1.1.0": questionnaireTemplateV1_1
};

// Define the current active version
export const CURRENT_QUESTIONNAIRE_VERSION = "1.1.0";

// Helper function to get a specific version of the template
export const getQuestionnaireTemplate = (version = null) => {
  // If no version specified, return the current active version
  const targetVersion = version || CURRENT_QUESTIONNAIRE_VERSION;
  
  // Return the requested version or fall back to current version
  return questionnaireVersions[targetVersion] || questionnaireVersions[CURRENT_QUESTIONNAIRE_VERSION];
};

// Helper function to get the latest active version
export const getLatestQuestionnaireTemplate = () => {
  return questionnaireVersions[CURRENT_QUESTIONNAIRE_VERSION];
};

// Helper function to get all available versions
export const getAvailableVersions = () => {
  return Object.keys(questionnaireVersions);
};

// Backward compatibility - export default template as the current version
export const questionnaireTemplate = getLatestQuestionnaireTemplate();








